import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from './Icon';
import MarkdownBody from './MarkdownBody';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import type { IngestionImpactSheetData } from '../hooks/useIngestionImpactFeed';

interface Props {
  visible: boolean;
  data: IngestionImpactSheetData | null;
  onClose: () => void;
  onReview?: () => void;
}

function CountPill({ label, count }: { label: string; count: number }) {
  const theme = useTheme();
  if (!count) return null;
  return (
    <View style={[styles.pill, { backgroundColor: theme.colors.primary + '18' }]}>
      <Text style={[styles.pillText, { color: theme.colors.primary }]}>
        +{count} {label}
      </Text>
    </View>
  );
}

export default function IngestionImpactSheet({ visible, data, onClose, onReview }: Props) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const sheet = useSheetLayout({ desktopMaxWidth: 520 });
  const { height: windowHeight } = useWindowDimensions();
  const [expanded, setExpanded] = useState(false);
  const [phase, setPhase] = useState<'processing' | 'reveal'>('processing');

  // ED-2169: only reset phase on visibility transitions. If `data` is
  // merged in place while the sheet is still open (trailing batches
  // arriving during a large upload), we want counts to update smoothly
  // instead of flickering back to the processing animation.
  React.useEffect(() => {
    if (!visible) return;
    setExpanded(false);
    setPhase('processing');
    const timer = setTimeout(() => {
      setPhase('reveal');
    }, 850);
    return () => clearTimeout(timer);
  }, [visible]);

  const fullSummary = data?.summaryMarkdown?.trim() || '';
  const previewSummary = data?.summaryPreview?.trim() || '';
  const hasSummary = fullSummary.length > 0;
  const showExpand = hasSummary && fullSummary.length > previewSummary.length;

  const title = useMemo(() => {
    const count = data?.fileCount || 0;
    if (phase === 'processing') {
      if (count <= 1) return 'File received';
      return `${count} files received`;
    }
    if (count <= 1) return 'Here is what that file gave us.';
    return `Here is what those ${count} files gave us.`;
  }, [data?.fileCount, phase]);

  const subtitle = phase === 'processing'
    ? 'Processing and extracting useful care context...'
    : 'Knowledge gained';
  const maxSheetHeight = Math.max(360, windowHeight - insets.top - 20);

  return (
    <Modal
      visible={visible && !!data}
      transparent
      animationType={sheet.animationType}
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <Pressable style={[styles.overlay, sheet.overlayStyle]} onPress={onClose}>
        <Pressable
          style={[
            styles.sheet,
            {
              backgroundColor: theme.colors.card,
              paddingBottom: Math.max(insets.bottom, 16),
              maxHeight: maxSheetHeight,
            },
            sheet.sheetStyle,
            sheet.desktopContentStyle,
          ]}
          onPress={() => {}}
        >
          {sheet.showHandle && <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />}
          <View style={styles.header}>
            <View style={[styles.iconWrap, { backgroundColor: theme.colors.primary + '18' }]}>
              <Icon icon={['fad', 'sparkles']} size={15} color={theme.colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.title, { color: theme.colors.text }]}>Knowledge Updated</Text>
              <Text style={[styles.subtitle, { color: theme.colors.muted }]}>{subtitle}</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Icon icon={['fas', 'xmark']} size={16} color={theme.colors.muted} />
            </TouchableOpacity>
          </View>
          <Text style={[styles.phaseLine, { color: theme.colors.text }]}>{title}</Text>

          {phase === 'processing' ? (
            <View style={styles.processingRow}>
              <ActivityIndicator size="small" color={theme.colors.primary} />
              <Text style={[styles.processingText, { color: theme.colors.muted }]}>
                Turning uploads into structured knowledge...
              </Text>
            </View>
          ) : (
            <ScrollView
              style={styles.revealScroll}
              contentContainerStyle={styles.revealScrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              <View style={styles.pillRow}>
                <CountPill label="contacts" count={data?.contacts || 0} />
                <CountPill label="meds" count={data?.medications || 0} />
                <CountPill label="events" count={data?.events || 0} />
                <CountPill label="inferences" count={data?.inferences || 0} />
                <CountPill label="memories" count={data?.memories || 0} />
              </View>

              {hasSummary && (
                <View style={[styles.summaryCard, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}>
                  <Text style={[styles.summaryLabel, { color: theme.colors.muted }]}>Generated summary</Text>
                  {expanded ? (
                    <MarkdownBody>{fullSummary}</MarkdownBody>
                  ) : (
                    <Text style={[styles.summaryPreviewText, { color: theme.colors.text }]} numberOfLines={6}>
                      {previewSummary}
                    </Text>
                  )}
                  {showExpand && (
                    <TouchableOpacity
                      style={styles.expandBtn}
                      onPress={() => setExpanded((v) => !v)}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.expandBtnText, { color: theme.colors.primary }]}>
                        {expanded ? 'View less' : 'View full'}
                      </Text>
                      <Icon
                        icon={['fas', expanded ? 'chevron-up' : 'chevron-down']}
                        size={11}
                        color={theme.colors.primary}
                      />
                    </TouchableOpacity>
                  )}
                </View>
              )}

              <Text style={[styles.valueText, { color: theme.colors.muted }]}>
                Your plan is now more complete and actionable.
              </Text>
              <View style={styles.ctaRow}>
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: theme.colors.primary }]}
                  onPress={onReview || onClose}
                  activeOpacity={0.7}
                >
                  <Text style={styles.primaryBtnText}>Review updates</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.secondaryBtn, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}
                  onPress={onClose}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.secondaryBtnText, { color: theme.colors.text }]}>Done</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.42)',
    justifyContent: 'flex-end',
  },
  sheet: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 10,
    paddingHorizontal: 16,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  phaseLine: {
    marginTop: 8,
    fontSize: 15,
    fontWeight: '700',
  },
  processingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  processingText: {
    fontSize: 13,
  },
  revealScroll: {
    marginTop: 12,
  },
  revealScrollContent: {
    paddingBottom: 4,
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  pill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  pillText: {
    fontSize: 12,
    fontWeight: '600',
  },
  summaryCard: {
    marginTop: 12,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 10,
  },
  summaryLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    marginBottom: 8,
    letterSpacing: 0.3,
  },
  summaryPreviewText: {
    fontSize: 13,
    lineHeight: 19,
  },
  expandBtn: {
    marginTop: 8,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  expandBtnText: {
    fontSize: 13,
    fontWeight: '600',
  },
  valueText: {
    marginTop: 10,
    fontSize: 12,
  },
  ctaRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
  },
  primaryBtn: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  secondaryBtn: {
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    paddingVertical: 11,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
